import { Component } from '@angular/core';
import { HttpService } from '../service/http.service';
import { PaymentReminder } from '../payment-reminder';
import * as moment from 'moment';
import { FormGroup, FormBuilder, FormControl, FormArray, Validators } from '@angular/forms';
import { BookingEmailTemplateListItem } from '../booking-email-template-list-item';
import { forkJoin } from 'rxjs';
import { Language } from '../language';
import { PaymentReminderTemplate } from '../payment-reminder-template';
import { PaymentReminderPost } from '../payment-reminder-post';
import { PrimeToastService } from '../prime-toast.service';
import { ApiError } from '../api-error';
import { ReminderTemplateForm } from '../form-template/reminder-template-form';
import { ReminderTemplateSubForm } from '../form-template/reminder-template-sub-form';
import { ReminderEmailForm } from '../form-template/reminder-email-form';
import { ReminderEmailSubForm } from '../form-template/reminder-email-sub-form';

@Component({
  selector: 'app-payment-reminders',
  templateUrl: './payment-reminders.component.html',
  styleUrls: ['./payment-reminders.component.css']
})
export class PaymentRemindersComponent {

  reminders: PaymentReminder[] = [];
  activeEmailSelection: boolean = false;
  activeTemplateConfiguration: boolean = false;
  emailForm: FormGroup<ReminderEmailForm>;
  templatesForm: FormGroup<ReminderTemplateForm>[];
  templateList: BookingEmailTemplateListItem[][] = [];
  reminderTemplates: PaymentReminderTemplate[] = [];
  languages: Language[] = [];
  selectedBookingId: number | null = null;
  sendingReminder: boolean = false;
  availableReminderTemplates: BookingEmailTemplateListItem[] = [];
  studentLanguage: Language | null = null;


  constructor(
    private httpService: HttpService,
    private builder: FormBuilder,
    private toastService: PrimeToastService,
  ) {
    this.updateReminderList();
    this.emailForm = new FormGroup<ReminderEmailForm>({
      emails: new FormArray<FormGroup>([]),
      template: new FormControl(null, Validators.required),
      withPastePixel: new FormControl(true, Validators.required)
    });
    this.templatesForm = [];
  }

  updateReminderList() {
    this.httpService.getPaymentReminders().subscribe(res => {
      this.reminders = res;
      for (let reminder of this.reminders) {
        if (reminder.requestLastSent) {
          let lastSentDate = moment(reminder.requestLastSent);
          let today = moment();
          reminder.daysSinceLastRequest = today.diff(lastSentDate, "days");
        }
      }
    });
  }

  addTemplateForm(i: number) {
    this.templatesForm[i].controls.templates.push(new FormGroup<ReminderTemplateSubForm>({
      template: new FormControl(null)
    }))
  }

  dismiss(reminder: PaymentReminder) {
    this.httpService.dismissPaymentReminder(reminder.id).subscribe(() => {
      this.updateReminderList();
    });
  }

  disable(reminder: PaymentReminder){
    this.httpService.disablePaymentReminder(reminder.id).subscribe(() => {
      this.updateReminderList();
    });
  }

  prepareReminderSending(reminder: PaymentReminder) {
    this.httpService.getStudentReminderInfoByBookingId(reminder.bookingId).subscribe(res => {
      switch (res.emails.length) {
        case 0:
          return;
        default:
          this.selectedBookingId = reminder.bookingId;
          this.activeEmailSelection = true;
          this.addEmailsToFormArray(res.emails);
          console.log(res);
          this.availableReminderTemplates = res.templates;
          this.studentLanguage = res.language;
      }
    });
  }

  addEmailsToFormArray(emails: string[]) {
    for (let email of emails) {
      this.emailForm.controls.emails.push(
        new FormGroup<ReminderEmailSubForm>({
          enabled: new FormControl(false, Validators.required),
          email: new FormControl(email, Validators.required)
        })
      );
    }
    if (emails.length === 1) {
      this.emailForm.controls.emails.at(0).get("enabled")?.setValue(true);
    }
  }

  activateTemplateForm() {
    this.activeTemplateConfiguration = true;
    forkJoin({
      reminderTemplates: this.httpService.getPaymentReminderTemplates(),
      languages: this.httpService.getLanguages(),
      emailTemplates: this.httpService.getBookingEmailTemplateListItems()
    }).subscribe(({ emailTemplates, languages, reminderTemplates }) => {
      if (this.templateList.length === 0) {
        this.initTemplateList(languages, emailTemplates);
        this.initTemplateFormArray(reminderTemplates);
      }
      this.reminderTemplates = reminderTemplates;
    });
  }

  initTemplateList(languages: Language[], emailTemplates: BookingEmailTemplateListItem[]) {
    let languageId = 1;
    let templateListIndex = 0;
    this.languages = languages;
    for (let language of languages) {
      this.templateList.push([]);
    }
    for (let i = 0; i < emailTemplates.length; i++) {
      if (emailTemplates[i].languageId != languageId) {
        languageId = emailTemplates[i].languageId;
        templateListIndex = languageId - 1;
      }
      this.templateList[templateListIndex].push(emailTemplates[i]);
    }
  }

  initTemplateFormArray(reminderTemplates: PaymentReminderTemplate[]) {
    let outerIndex = 0;
    for (let reminderTemplate of reminderTemplates) {
      let templates = [];
      let templateForm = new FormGroup<ReminderTemplateForm>({
        templates: new FormArray<FormGroup>([])
      });
      if (reminderTemplate.templateIds) {
        for (let i = 0; i < reminderTemplate.templateIds.length; i++) {
          let id = reminderTemplate.templateIds[i];
          let template = this.getTemplateById(id, reminderTemplate.language.id);
          templates.push(template);
          if (id) {
            templateForm.controls.templates.push(
              new FormGroup<ReminderTemplateSubForm>({
                template: new FormControl(this.getTemplateById(id, reminderTemplate.language.id))
              }));
          }
        }
      }
      this.templatesForm.push(templateForm);
      outerIndex++;
    }
  }

  updateTemplates() {
    for(let i = 0; i < this.templatesForm.length; i++){
      this.reminderTemplates[i].templateIds = [];
      for(let template of this.templatesForm[i].controls.templates.controls){
        this.reminderTemplates[i].templateIds.push(template.value.template.id);
      }
    }
    this.httpService.updatePaymentReminderTemplates(this.reminderTemplates).subscribe(() => {
      this.activeTemplateConfiguration = false;
    });
  }

  getTemplateById(templateId: number, languageId: number) {
    let index = languageId - 1;
    for (let template of this.templateList[index]) {
      if (template.id === templateId) {
        return template;
      }
    }
    console.log("template not found: " + templateId + ", language id: " + languageId);
    return null;
  }

  sendReminder(destinationEmails: string[], withPastePixel: boolean, bookingId: number, templateId: number) {
    let reminderInfo = new PaymentReminderPost(bookingId, destinationEmails, withPastePixel, templateId);
    this.sendingReminder = true;
    this.httpService.sendPaymentReminderByEmail(reminderInfo).subscribe(() => {
      this.toastService.addSuccess("Success", "Reminder sent");
      this.sendingReminder = false;
      this.activeEmailSelection = false;
      this.updateReminderList();
    }, error => {
      let apiError = new ApiError(error);
      this.toastService.addError("Error", apiError.message);
      this.sendingReminder = false;
    });
  }

  sendReminderFromDialog() {
    let emailFormArray = this.emailForm.controls.emails;
    let emails: string[] = [];
    if (this.selectedBookingId) {
      let bookingId = this.selectedBookingId;
      for (let group of emailFormArray.controls) {
        if (group.value.enabled) {
          emails.push(group.value.email);
        }
      }
      if (emails.length == 0) {
        this.activeEmailSelection = false;
      } else {
        this.sendReminder(emails, this.emailForm.value.withPastePixel!, bookingId, this.emailForm.value.template!.id);
      }
    }
  }

  clearEmailsForm() {
    this.emailForm.controls.emails.clear();
  }

  deleteTemplateForm(i: number, j: number){
    this.templatesForm[i].controls.templates.removeAt(j);
  }

}
